import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Star,
  Pencil,
  X,
  ImagePlus,
  Search,
} from "lucide-react";
import { http, rupiah, apiError } from "@/lib/api";
import AdminShell, { PageHead } from "@/components/AdminShell";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import MasterDataTransfer from "@/components/MasterDataTransfer";

const empty = {
  name: "",
  description: "",
  price: "",
  category_id: "",
  image_url: "",
  image_position_x: 50,
  image_position_y: 50,
  image_zoom: 1,
  available: true,
  featured: false,
  stock_recipe: [],
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryBusy, setCategoryBusy] = useState(false);
  const imageInputRef = useRef(null);
  const imageAdjustRef = useRef(null);
  const imageAdjustPointerRef = useRef(null);

  const isOwner = me?.role === "owner";

  async function load() {
    try {
      const [p, c, inv, mods, user] = await Promise.all([
        http.get("/admin/products"),
        http.get("/admin/categories"),
        http.get("/admin/inventory"),
        http.get("/admin/modifiers"),
        http.get("/auth/me"),
      ]);

      setProducts(p.data);
      setCats(c.data);
      setInventory(inv.data || []);
      setModifiers(mods.data || []);
      setMe(user.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openNew() {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh menambah produk.");
      return;
    }

    setIsDraggingImage(false);
    setEditing({
      ...empty,
      category_id: cats[0]?.id || "",
    });
  }

  async function onImage(file) {
    if (!file) return;

    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh mengubah produk.");
      return;
    }

    const allowedExtensions = [
      ".jpg",
      ".jpeg",
      ".jfif",
      ".png",
      ".webp",
      ".bmp",
      ".gif",
    ];

    const fileName = String(file.name || "").toLowerCase();
    const extensionAllowed = allowedExtensions.some((ext) =>
      fileName.endsWith(ext),
    );

    const mimeAllowed =
      String(file.type || "").startsWith("image/") ||
      extensionAllowed;

    if (!mimeAllowed || !extensionAllowed) {
      toast.error(
        "Format gambar harus JPG, JPEG, JFIF, PNG, WEBP, BMP, atau GIF.",
      );
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 8MB.");
      return;
    }

    const readAsDataUrl = () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () =>
          resolve(String(reader.result || ""));

        reader.onerror = () =>
          reject(new Error("Gagal membaca gambar."));

        reader.readAsDataURL(file);
      });

    try {
      const sourceDataUrl = await readAsDataUrl();

      // GIF dibiarkan apa adanya supaya tetap kompatibel.
      if (fileName.endsWith(".gif")) {
        setEditing((current) =>
          current
            ? {
                ...current,
                image_url: sourceDataUrl,
                image_position_x: 50,
                image_position_y: 50,
                image_zoom: 1,
              }
            : current,
        );
        return;
      }

      // Format gambar umum dinormalisasi ke WEBP agar lebih ringan
      // dan konsisten untuk ditampilkan di menu customer.
      const image = new Image();

      const normalizedDataUrl = await new Promise(
        (resolve, reject) => {
          image.onload = () => {
            try {
              const maxSide = 1600;
              const width = image.naturalWidth || image.width;
              const height = image.naturalHeight || image.height;

              if (!width || !height) {
                reject(
                  new Error("Ukuran gambar tidak dapat dibaca."),
                );
                return;
              }

              const scale = Math.min(
                1,
                maxSide / Math.max(width, height),
              );

              const targetWidth = Math.max(
                1,
                Math.round(width * scale),
              );
              const targetHeight = Math.max(
                1,
                Math.round(height * scale),
              );

              const canvas =
                document.createElement("canvas");

              canvas.width = targetWidth;
              canvas.height = targetHeight;

              const ctx = canvas.getContext("2d");

              if (!ctx) {
                reject(
                  new Error("Browser tidak mendukung konversi gambar."),
                );
                return;
              }

              // Background putih menjaga PNG/BMP transparan tetap aman
              // ketika dikonversi ke format produk.
              ctx.fillStyle = "#ffffff";
              ctx.fillRect(
                0,
                0,
                targetWidth,
                targetHeight,
              );

              ctx.drawImage(
                image,
                0,
                0,
                targetWidth,
                targetHeight,
              );

              resolve(
                canvas.toDataURL("image/webp", 0.9),
              );
            } catch (error) {
              reject(error);
            }
          };

          image.onerror = () =>
            reject(
              new Error(
                "Format gambar ini tidak dapat dibaca oleh browser.",
              ),
            );

          image.src = sourceDataUrl;
        },
      );

      setEditing((current) =>
        current
          ? {
              ...current,
              image_url: normalizedDataUrl,
              image_position_x: 50,
              image_position_y: 50,
              image_zoom: 1,
            }
          : current,
      );
    } catch (error) {
      toast.error(
        error?.message ||
          "Gagal memproses gambar.",
      );
    }
  }

  function handleImageDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!isOwner) return;
    setIsDraggingImage(true);
  }

  function handleImageDragOver(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!isOwner) return;

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }

    setIsDraggingImage(true);
  }

  function handleImageDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!isOwner) return;

    const current = e.currentTarget;
    const related = e.relatedTarget;

    if (related && current.contains(related)) {
      return;
    }

    setIsDraggingImage(false);
  }

  function handleImageDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImage(false);

    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh mengubah produk.");
      return;
    }

    const file = e.dataTransfer?.files?.[0];

    if (!file) {
      toast.error("Tidak ada file gambar yang dipilih.");
      return;
    }

    onImage(file);
  }


  function clampImageValue(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function productImageStyle(product) {
    const x = clampImageValue(
      Number(product?.image_position_x ?? 50),
      0,
      100,
    );
    const y = clampImageValue(
      Number(product?.image_position_y ?? 50),
      0,
      100,
    );
    const zoom = clampImageValue(
      Number(product?.image_zoom ?? 1),
      1,
      2.5,
    );

    return {
      objectPosition: `${x}% ${y}%`,
      transform: `scale(${zoom})`,
      transformOrigin: `${x}% ${y}%`,
    };
  }

  function beginImageAdjust(e) {
    if (!editing?.image_url) return;

    e.preventDefault();
    e.stopPropagation();

    const frame = imageAdjustRef.current;
    if (!frame) return;

    frame.setPointerCapture?.(e.pointerId);

    imageAdjustPointerRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      positionX: Number(editing.image_position_x ?? 50),
      positionY: Number(editing.image_position_y ?? 50),
    };
  }

  function moveImageAdjust(e) {
    const drag = imageAdjustPointerRef.current;
    const frame = imageAdjustRef.current;

    if (!drag || !frame || drag.pointerId !== e.pointerId) return;

    e.preventDefault();

    const rect = frame.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;

    const nextX = clampImageValue(
      drag.positionX - (dx / rect.width) * 100,
      0,
      100,
    );
    const nextY = clampImageValue(
      drag.positionY - (dy / rect.height) * 100,
      0,
      100,
    );

    setEditing((current) =>
      current
        ? {
            ...current,
            image_position_x: Math.round(nextX * 10) / 10,
            image_position_y: Math.round(nextY * 10) / 10,
          }
        : current,
    );
  }

  function endImageAdjust(e) {
    const drag = imageAdjustPointerRef.current;

    if (!drag || drag.pointerId !== e.pointerId) return;

    imageAdjustRef.current?.releasePointerCapture?.(e.pointerId);
    imageAdjustPointerRef.current = null;
  }

  function resetImageAdjust() {
    setEditing((current) =>
      current
        ? {
            ...current,
            image_position_x: 50,
            image_position_y: 50,
            image_zoom: 1,
          }
        : current,
    );
  }

  function addRecipeRow() {
    const firstInventory = inventory[0];
    const firstModifierGroup = modifiers.find(
      (group) =>
        (group.options || []).some((option) => {
          const usage = option.stock_usage || {};
          return (
            option.active !== false &&
            usage.inventory_id &&
            Number(usage.qty) > 0
          );
        }),
    );

    if (!firstInventory && !firstModifierGroup) {
      toast.error(
        "Belum ada Bahan Stok atau Grup Modifier yang memiliki mapping stok.",
      );
      return;
    }

    const firstRow = firstInventory
      ? {
          source_type: "inventory",
          inventory_id: firstInventory.id,
          qty: 1,
        }
      : {
          source_type: "modifier_group",
          modifier_group_id: firstModifierGroup.id,
          qty: 1,
        };

    setEditing((current) => ({
      ...current,
      stock_recipe: [
        ...(current?.stock_recipe || []),
        firstRow,
      ],
    }));
  }


  function updateRecipeRow(index, patch) {
    setEditing((current) => ({
      ...current,
      stock_recipe: (current?.stock_recipe || []).map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      ),
    }));
  }

  function removeRecipeRow(index) {
    setEditing((current) => ({
      ...current,
      stock_recipe: (current?.stock_recipe || []).filter(
        (_, i) => i !== index,
      ),
    }));
  }

  function toTitleCase(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  async function addCategoryInline() {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh menambah kategori.");
      return;
    }

    const cleanName = toTitleCase(newCategoryName.trim());

    if (!cleanName) {
      toast.error("Nama kategori wajib diisi.");
      return;
    }

    setCategoryBusy(true);

    try {
      const { data: created } = await http.post(
        "/admin/categories",
        { name: cleanName },
      );

      const { data: latestCategories } = await http.get(
        "/admin/categories",
      );

      const nextCategories = latestCategories || [];
      setCats(nextCategories);

      const selectedId =
        created?.id ||
        nextCategories.find(
          (c) =>
            String(c.name || "").trim().toLowerCase() ===
            cleanName.toLowerCase(),
        )?.id ||
        "";

      if (selectedId) {
        setEditing((current) =>
          current
            ? {
                ...current,
                category_id: selectedId,
              }
            : current,
        );
      }

      setNewCategoryName("");
      setAddingCategory(false);
      toast.success("Kategori ditambahkan");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCategoryBusy(false);
    }
  }

  const modifierStockGroups = modifiers
    .filter((group) =>
      (group.options || []).some((option) => {
        const usage = option.stock_usage || {};
        return (
          option.active !== false &&
          usage.inventory_id &&
          Number(usage.qty) > 0
        );
      }),
    )
    .map((group) => ({
      id: group.id,
      name: group.name,
      min_select: Number(group.min_select || 0),
      max_select: Number(group.max_select || 1),
    }));


  function encodeRecipeSource(row) {
    if (
      (row.source_type || "inventory") === "modifier_group" ||
      (row.source_type || "inventory") === "modifier"
    ) {
      return `modifier_group:${row.modifier_group_id || ""}`;
    }

    return `inventory:${row.inventory_id || ""}`;
  }

  function changeRecipeSource(index, value) {
    if (value.startsWith("modifier_group:")) {
      const groupId = value.slice(
        "modifier_group:".length,
      );

      const selected = modifierStockGroups.find(
        (group) => group.id === groupId,
      );

      if (!selected) return;

      updateRecipeRow(index, {
        source_type: "modifier_group",
        modifier_group_id: selected.id,
        modifier_option_id: "",
        inventory_id: "",
        qty: 1,
      });

      return;
    }

    const inventoryId = value.slice(
      "inventory:".length,
    );

    updateRecipeRow(index, {
      source_type: "inventory",
      inventory_id: inventoryId,
      modifier_group_id: "",
      modifier_option_id: "",
      qty: 1,
    });
  }


  async function save() {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh mengubah produk.");
      return;
    }

    if (!editing) return;

    const body = {
      ...editing,
      name: toTitleCase(editing.name.trim()),
      available: true,
      price: parseInt(editing.price, 10) || 0,
      stock_recipe: (editing.stock_recipe || [])
        .filter((row) => {
          const sourceType =
            row.source_type || "inventory";

          if (
            sourceType === "modifier_group" ||
            sourceType === "modifier"
          ) {
            return Boolean(row.modifier_group_id);
          }

          return (
            row.inventory_id &&
            Number(row.qty) > 0
          );
        })
        .map((row) => {
          const sourceType =
            row.source_type || "inventory";

          if (
            sourceType === "modifier_group" ||
            sourceType === "modifier"
          ) {
            return {
              source_type: "modifier_group",
              modifier_group_id:
                row.modifier_group_id,
            };
          }

          return {
            source_type: "inventory",
            inventory_id:
              row.inventory_id,
            qty: Number(row.qty),
          };
        }),
    };

    const originalProduct = editing.id
      ? products.find((product) => product.id === editing.id)
      : null;

    const originalRecipe =
      originalProduct?.stock_recipe || [];

    // Produk baru tetap WAJIB punya Komposisi Stok.
    if (!editing.id && !body.stock_recipe.length) {
      toast.error(
        "Komposisi Stok wajib diisi minimal 1 sumber: Bahan Stok atau Modifier.",
      );
      return;
    }

    // Produk lama yang sejak awal memang belum punya Komposisi Stok
    // tetap boleh diedit nama, kategori, harga, gambar, atau favorit.
    // Jangan kirim stock_recipe kosong ke backend.
    if (
      editing.id &&
      !body.stock_recipe.length &&
      !originalRecipe.length
    ) {
      delete body.stock_recipe;
    }

    // Kalau sebelumnya sudah punya Komposisi Stok,
    // komposisi tersebut tidak boleh dihapus sampai kosong.
    if (
      editing.id &&
      Array.isArray(body.stock_recipe) &&
      !body.stock_recipe.length &&
      originalRecipe.length
    ) {
      toast.error(
        "Komposisi Stok tidak boleh dikosongkan.",
      );
      return;
    }

    try {
      if (editing.id) {
        await http.patch(`/admin/products/${editing.id}`, body);
      } else {
        await http.post("/admin/products", body);
      }

      toast.success("Produk disimpan");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function askDelete(product) {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh menghapus produk.");
      return;
    }
    setDeleteTarget(product);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await http.delete(`/admin/products/${deleteTarget.id}`);
      toast.success("Produk dihapus");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  const filteredProducts = products.filter((p) =>
    `${p.name} ${p.category_name || ""}`
      .toLowerCase()
      .includes(q.trim().toLowerCase()),
  );

  const collator = new Intl.Collator("id-ID", {
    numeric: true,
    sensitivity: "base",
  });

  const groupedProducts = Object.entries(
    filteredProducts.reduce((acc, product) => {
      const categoryName =
        String(product.category_name || "Tanpa Kategori").trim() ||
        "Tanpa Kategori";

      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }

      acc[categoryName].push(product);
      return acc;
    }, {}),
  )
    .sort(([categoryA], [categoryB]) =>
      collator.compare(categoryA, categoryB),
    )
    .map(([categoryName, items]) => [
      categoryName,
      [...items].sort((a, b) =>
        collator.compare(
          String(a.name || ""),
          String(b.name || ""),
        ),
      ),
    ]);

  return (
    <AdminShell>
      <PageHead
        eyebrow="Katalog"
        title="Produk"
        desc={isOwner ? "Kelola daftar menu." : "Lihat daftar produk."}
      >
        {isOwner && (
          <div className="flex flex-wrap items-center gap-2">
            <MasterDataTransfer
              kind="products"
              label="Produk"
              onImported={load}
            />

            <button
              data-testid="add-product-btn"
              onClick={openNew}
              className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition-transform active:scale-95"
            >
              <Plus size={16} />
              Produk
            </button>
          </div>
        )}
      </PageHead>

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-card px-3">
        <Search size={16} className="text-muted-foreground" />
        <input
          data-testid="product-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari produk / kategori"
          className="w-full bg-transparent py-2.5 text-sm outline-none"
        />
      </div>

      {loading ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Memuat produk...
        </div>
      ) : groupedProducts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Tidak ada produk.
        </div>
      ) : (
        <div className="space-y-8">
          {groupedProducts.map(([categoryName, items]) => (
            <section key={categoryName}>
              <div className="mb-3 flex items-center gap-3">
                <h2 className="font-heading text-lg font-extrabold text-card-foreground">
                  {categoryName}
                </h2>
                <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-black text-muted-foreground">
                  {items.length} produk
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`admin-product-${p.id}`}
                    className="overflow-hidden rounded-3xl border border-border bg-card"
                  >
                    <div className="h-36 bg-muted">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="h-full w-full object-cover"
                          style={productImageStyle(p)}
                        />
                      ) : (
                        <div className="grid h-full place-items-center text-muted-foreground">
                          <ImagePlus size={24} />
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 font-heading text-sm font-bold text-card-foreground">
                            {p.name}
                            {p.featured && (
                              <Star
                                size={13}
                                className="fill-primary text-primary"
                              />
                            )}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            {p.category_name}
                          </div>
                        </div>

                        <b className="font-heading text-sm text-primary">
                          {rupiah(p.price)}
                        </b>
                      </div>

                      {isOwner && (
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={() => {
                              setIsDraggingImage(false);
                              setEditing({
                                ...empty,
                                ...p,
                                price: String(p.price),
                              });
                            }}
                            className="flex flex-1 items-center justify-center gap-1 rounded-xl bg-muted py-2 text-xs font-bold"
                          >
                            <Pencil size={13} />
                            Edit
                          </button>

                          <button
                            data-testid={`del-product-${p.id}`}
                            onClick={() => askDelete(p)}
                            className="grid h-9 w-9 place-items-center rounded-xl bg-destructive/10 text-destructive"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && isOwner && (
        <Modal
          onClose={() => {
            setIsDraggingImage(false);
            setEditing(null);
          }}
          title={editing.id ? "Edit Produk" : "Produk Baru"}
        >
          <div className="space-y-3">
            <div
              data-testid="product-image-dropzone"
              onDragEnter={handleImageDragEnter}
              onDragOver={handleImageDragOver}
              onDragLeave={handleImageDragLeave}
              onDrop={handleImageDrop}
              onClick={() => imageInputRef.current?.click()}
              className={`group relative h-44 cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed transition-all ${
                isDraggingImage
                  ? "border-primary bg-primary/10 ring-2 ring-primary/20"
                  : "border-border bg-muted hover:border-primary/60"
              }`}
            >
              {editing.image_url ? (
                <>
                  <img
                    src={editing.image_url}
                    alt="preview"
                    className={`h-full w-full object-cover transition ${
                      isDraggingImage ? "opacity-55" : ""
                    }`}
                    style={productImageStyle(editing)}
                  />

                  <div
                    className={`absolute inset-0 grid place-items-center bg-black/45 transition-opacity ${
                      isDraggingImage
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100"
                    }`}
                  >
                    <div className="text-center text-white">
                      <ImagePlus className="mx-auto mb-2" size={28} />
                      <div className="text-sm font-bold">
                        {isDraggingImage
                          ? "Lepaskan gambar di sini"
                          : "Klik atau drag gambar baru"}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex h-full flex-col items-center justify-center px-5 text-center">
                  <div
                    className={`mb-3 grid h-12 w-12 place-items-center rounded-2xl transition ${
                      isDraggingImage
                        ? "bg-primary text-primary-foreground"
                        : "bg-background/70 text-muted-foreground"
                    }`}
                  >
                    <ImagePlus size={23} />
                  </div>

                  <div className="text-sm font-bold text-card-foreground">
                    {isDraggingImage
                      ? "Lepaskan gambar di sini"
                      : "Drag & drop gambar produk"}
                  </div>

                  <div className="mt-1 text-xs text-muted-foreground">
                    atau klik area ini untuk memilih gambar
                  </div>

                  <div className="mt-2 text-[11px] text-muted-foreground">
                    JPG, JPEG, JFIF, PNG, WEBP, BMP, GIF · Maksimal 8MB
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-muted py-2.5 text-sm font-bold transition hover:bg-muted/80"
            >
              <ImagePlus size={16} />
              {editing.image_url ? "Ganti Gambar" : "Pilih Gambar"}
            </button>

            <input
              ref={imageInputRef}
              type="file"
              accept="image/*,.jpg,.jpeg,.jfif,.png,.webp,.bmp,.gif"
              hidden
              onChange={(e) => {
                onImage(e.target.files?.[0]);
                e.target.value = "";
              }}
            />


            {editing.image_url && (
              <div className="rounded-2xl border border-border bg-muted/40 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-card-foreground">
                      Atur Tampilan Gambar Customer
                    </div>
                    <div className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                      Drag gambar ke kiri / kanan / atas / bawah. Preview Grid memakai tinggi yang sama seperti kartu customer.
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={resetImageAdjust}
                    className="flex-none rounded-lg bg-background px-2.5 py-1.5 text-[11px] font-bold text-card-foreground"
                  >
                    Reset
                  </button>
                </div>

                <div className="mt-3 grid gap-4 sm:grid-cols-[minmax(0,1fr)_112px]">
                  <div>
                    <div className="mb-1.5 text-[10px] font-black uppercase tracking-wide text-muted-foreground">
                      Preview Grid Customer
                    </div>

                    <div className="mx-auto w-full max-w-[218px] overflow-hidden rounded-3xl border border-border bg-card">
                      <div
                        ref={imageAdjustRef}
                        onPointerDown={beginImageAdjust}
                        onPointerMove={moveImageAdjust}
                        onPointerUp={endImageAdjust}
                        onPointerCancel={endImageAdjust}
                        className="relative h-28 w-full cursor-grab touch-none overflow-hidden bg-muted active:cursor-grabbing"
                        title="Drag gambar untuk mengatur posisi"
                      >
                        <img
                          src={editing.image_url}
                          alt="Preview Grid Customer"
                          draggable={false}
                          className="pointer-events-none h-full w-full select-none object-cover"
                          style={productImageStyle(editing)}
                        />

                        <div className="pointer-events-none absolute inset-x-0 bottom-1 text-center">
                          <span className="rounded-full bg-black/55 px-2 py-1 text-[9px] font-bold text-white">
                            Drag untuk geser
                          </span>
                        </div>
                      </div>

                      <div className="p-3">
                        <div className="truncate text-sm font-bold text-card-foreground">
                          {editing.name || "Nama Produk"}
                        </div>
                        <div className="mt-1 font-heading text-base font-extrabold text-primary">
                          {rupiah(Number(editing.price) || 0)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="mb-1.5 text-[10px] font-black uppercase tracking-wide text-muted-foreground">
                      Preview List
                    </div>

                    <div className="h-24 w-24 overflow-hidden rounded-2xl border border-border bg-muted">
                      <img
                        src={editing.image_url}
                        alt="Preview List Customer"
                        draggable={false}
                        className="h-full w-full object-cover"
                        style={productImageStyle(editing)}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="mb-1 flex items-center justify-between gap-3 text-[11px]">
                    <span className="font-bold text-card-foreground">Zoom</span>
                    <span className="font-mono text-muted-foreground">
                      {Number(editing.image_zoom ?? 1).toFixed(2)}×
                    </span>
                  </div>

                  <input
                    type="range"
                    min="1"
                    max="2.5"
                    step="0.05"
                    value={Number(editing.image_zoom ?? 1)}
                    onChange={(e) =>
                      setEditing((current) =>
                        current
                          ? {
                              ...current,
                              image_zoom: Number(e.target.value),
                            }
                          : current,
                      )
                    }
                    className="w-full accent-current"
                  />

                  <div className="mt-1 text-center text-[10px] text-muted-foreground">
                    Posisi X {Math.round(Number(editing.image_position_x ?? 50))}% · Y {Math.round(Number(editing.image_position_y ?? 50))}%
                  </div>
                </div>
              </div>
            )}

            <Field label="Nama">
              <input
                data-testid="product-name-input"
                value={editing.name}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    name: toTitleCase(e.target.value),
                  })
                }
                className="input"
              />
            </Field>

            <Field label="Deskripsi">
              <textarea
                value={editing.description}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    description: e.target.value,
                  })
                }
                rows={2}
                className="input resize-none"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Harga (Rp)">
                <input
                  data-testid="product-price-input"
                  type="number"
                  value={editing.price}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      price: e.target.value,
                    })
                  }
                  className="input"
                />
              </Field>

              <Field label="Kategori">
                <div className="flex gap-2">
                  <select
                    value={editing.category_id}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        category_id: e.target.value,
                      })
                    }
                    className="input min-w-0 flex-1"
                  >
                    {cats.length === 0 && (
                      <option value="">
                        Belum ada kategori
                      </option>
                    )}

                    {cats.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => {
                      setNewCategoryName("");
                      setAddingCategory(true);
                    }}
                    className="grid h-[42px] w-[42px] flex-none place-items-center rounded-xl bg-primary text-primary-foreground transition-transform active:scale-95"
                    title="Tambah kategori"
                    aria-label="Tambah kategori"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </Field>
            </div>

            <div className="rounded-2xl border border-border bg-muted/40 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-bold text-card-foreground">
                    Komposisi Stok
                  </div>
                  <div className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                    Wajib minimal 1 komposisi. Bisa pilih Bahan Stok langsung
                    atau judul Grup Modifier. Stok modifier mengikuti pilihan pelanggan.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={addRecipeRow}
                  className="flex flex-none items-center gap-1 rounded-xl bg-primary px-3 py-2 text-xs font-bold text-primary-foreground"
                >
                  <Plus size={13} />
                  Bahan
                </button>
              </div>

              <div className="mt-3 space-y-2">
                {(editing.stock_recipe || []).map((row, index) => {
                  const selected = inventory.find(
                    (item) =>
                      item.id === row.inventory_id &&
                      (row.source_type || "inventory") === "inventory",
                  );

                  return (
                    <div
                      key={`${row.inventory_id || "row"}-${index}`}
                      className="grid grid-cols-[minmax(0,1fr)_120px_38px] gap-2"
                    >
                      <select
                        value={encodeRecipeSource(row)}
                        onChange={(e) =>
                          changeRecipeSource(index, e.target.value)
                        }
                        className="input"
                      >
                        <optgroup label="Bahan Stok">
                          {inventory.map((item) => (
                            <option
                              key={`inventory-${item.id}`}
                              value={`inventory:${item.id}`}
                            >
                              {item.name} · {item.stock} {item.unit}
                            </option>
                          ))}
                        </optgroup>

                        {modifierStockGroups.length > 0 && (
                          <optgroup label="Grup Modifier / Topping">
                            {modifierStockGroups.map((group) => (
                              <option
                                key={`modifier-group-${group.id}`}
                                value={`modifier_group:${group.id}`}
                              >
                                {group.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>

                      {(
                        (row.source_type || "inventory") === "modifier_group" ||
                        (row.source_type || "inventory") === "modifier"
                      ) ? (
                        <div className="flex items-center rounded-xl border border-border bg-muted px-3 text-xs font-semibold text-muted-foreground">
                          Sesuai pilihan
                        </div>
                      ) : (
                        <div className="relative">
                          <input
                            type="number"
                            min="0.001"
                            step="0.001"
                            value={row.qty ?? 1}
                            onChange={(e) =>
                              updateRecipeRow(index, {
                                qty: e.target.value,
                              })
                            }
                            className="input pr-12"
                            placeholder="Qty"
                          />
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                            {selected?.unit || ""}
                          </span>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => removeRecipeRow(index)}
                        className="grid h-10 w-10 place-items-center rounded-xl bg-destructive/10 text-destructive"
                        title="Hapus bahan"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}

                {(editing.stock_recipe || []).length === 0 && (
                  <div className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                    Komposisi Stok wajib diisi. Tambahkan minimal 1 Bahan Stok atau judul Grup Modifier / Topping.
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <Toggle
                label="Favorit"
                checked={editing.featured}
                onChange={(v) =>
                  setEditing({
                    ...editing,
                    featured: v,
                  })
                }
              />
            </div>

            <button
              data-testid="save-product-btn"
              onClick={save}
              className="w-full rounded-xl bg-primary py-3 font-bold text-primary-foreground transition-transform active:scale-95"
            >
              Simpan
            </button>
          </div>
        </Modal>
      )}

      {addingCategory && (
        <Modal
          title="Tambah Kategori"
          onClose={() => {
            if (categoryBusy) return;
            setAddingCategory(false);
            setNewCategoryName("");
          }}
        >
          <div className="space-y-3">
            <Field label="Nama Kategori">
              <input
                autoFocus
                value={newCategoryName}
                onChange={(e) =>
                  setNewCategoryName(
                    toTitleCase(e.target.value),
                  )
                }
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    !categoryBusy
                  ) {
                    addCategoryInline();
                  }
                }}
                placeholder="Contoh: Minuman"
                className="input"
              />
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={categoryBusy}
                onClick={() => {
                  setAddingCategory(false);
                  setNewCategoryName("");
                }}
                className="rounded-xl bg-muted py-3 text-sm font-bold text-card-foreground disabled:opacity-50"
              >
                Batal
              </button>

              <button
                type="button"
                disabled={categoryBusy}
                onClick={addCategoryInline}
                className="rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground disabled:opacity-60"
              >
                {categoryBusy
                  ? "Menyimpan..."
                  : "Tambah"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title="Hapus Produk?"
        message="Produk akan dihapus dari katalog. Tindakan ini tidak dapat dibatalkan."
        itemName={deleteTarget?.name || ""}
        busy={deleteBusy}
        onCancel={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <style>{`
        .input {
          width: 100%;
          border: 1px solid hsl(var(--border));
          background: hsl(var(--muted));
          border-radius: .75rem;
          padding: .65rem .8rem;
          font-size: .85rem;
          outline: none;
          color: hsl(var(--card-foreground));
        }
      `}</style>
    </AdminShell>
  );
}

export function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className="relative max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-t-3xl border border-border bg-card p-6 sm:rounded-3xl"
        style={{ animation: "wm-sheet .25s ease" }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl font-extrabold text-card-foreground">
            {title}
          </h2>

          <button
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-muted"
          >
            <X size={18} />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

export function Toggle({ label, checked, onChange }) {
  return (
    <button
      type="button"
      data-testid="toggle"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2.5 text-sm font-semibold text-card-foreground"
    >
      <span
        className={`relative inline-flex h-7 w-[52px] flex-none items-center rounded-full border transition-colors ${
          checked
            ? "border-primary bg-primary"
            : "border-border bg-muted"
        }`}
      >
        <span
          className={`absolute h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-black/10 transition-transform ${
            checked ? "translate-x-[26px]" : "translate-x-1"
          }`}
        />
      </span>

      {label && <span>{label}</span>}

      <span
        className={`text-[10px] font-black uppercase tracking-wide ${
          checked ? "text-primary" : "text-muted-foreground"
        }`}
      >
        {checked ? "On" : "Off"}
      </span>
    </button>
  );
}